import { McpOwnerPanel } from '@/features/mcp/McpOwnerPanel';
import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Building2,
  ShieldCheck,
  ChevronRight,
  Activity,
  TriangleAlert,
  LayoutGrid,
  CreditCard,
  Package,
  Puzzle,
  Users,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Plus,
  Wallet,
  KeyRound,
  Search,
  UserCheck,
  UserX,
  Trash2,
} from 'lucide-react';
import { ConfirmDialog, useDiscardGuard } from '@/components/ui/confirm-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  tenantAdminService,
  TenantSummary,
  TenantHealthReport,
  TenantAdminUser,
} from '@/features/branding/services/tenant-admin.service';
import { FeatureFlag } from '@/features/branding/services/feature-flags.service';
import {
  subscriptionsAdminService,
  BillingSummary,
  Plan,
  PlanAddon,
  TenantAddon,
} from '@/features/branding/services/subscriptions.service';
import { FeatureModuleExpandableRow } from '@/components/system/FeatureModuleExpandableRow';

export const Route = createFileRoute('/system/tenants')({
  component: SystemTenantsPage,
});

type DetailTab = 'features' | 'subscription' | 'health' | 'users';

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// ─── Dialog: Deletar Tenant ───────────────────────────────────────────────────

export function DeleteTenantDialog({
  tenant,
  open,
  onOpenChange,
  onDeleted,
}: {
  tenant: TenantSummary;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await tenantAdminService.deleteTenant(tenant.tenantId);
      toast.success(`Tenant "${tenant.tenantName}" excluído permanentemente.`);
      qc.invalidateQueries({ queryKey: ['system', 'tenants'] });
      onOpenChange(false);
      onDeleted();
    } catch {
      // toast de erro vem do interceptor
    } finally {
      setDeleting(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Excluir tenant “${tenant.tenantName}” permanentemente?`}
      description={`Esta ação é irreversível. Todos os dados do tenant (${tenant.tenantSlug}) — usuários, arquivos, configurações e histórico — serão excluídos permanentemente do banco de dados.`}
      confirmLabel="Excluir tenant"
      destructive
      loading={deleting}
      typeToConfirm={tenant.tenantSlug}
      onConfirm={handleDelete}
    />
  );
}

// ─── Dialog: Criar Tenant ─────────────────────────────────────────────────────

function CreateTenantDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [planId, setPlanId] = useState('');

  const { data: plans = [] } = useQuery<Plan[]>({
    queryKey: ['system', 'plans'],
    queryFn: () => subscriptionsAdminService.listPlans(),
    enabled: open,
  });

  useEffect(() => {
    if (!slugManual) setSlug(generateSlug(name));
  }, [name, slugManual]);

  const handleClose = () => {
    setName(''); setSlug(''); setSlugManual(false);
    setAdminEmail(''); setAdminPassword(''); setPlanId('');
    onOpenChange(false);
  };

  const createMutation = useMutation({
    mutationFn: () =>
      tenantAdminService.createTenant({
        name,
        slug: slug || undefined,
        adminEmail,
        adminPassword,
        planId: planId || undefined,
      }),
    onSuccess: (data) => {
      toast.success(`Tenant "${data.tenant.name}" criado com sucesso!`);
      qc.invalidateQueries({ queryKey: ['system', 'tenants'] });
      handleClose();
    },
  });

  const guardedOpenChange = useDiscardGuard(
    !!(name.trim() || adminEmail.trim() || adminPassword || planId),
    (o) => !o && handleClose(),
  );

  const canSubmit = name.trim().length >= 2 && adminEmail.includes('@') && adminPassword.length >= 8;

  return (
    <Dialog open={open} onOpenChange={guardedOpenChange}>
      <DialogContent size="md" busy={createMutation.isPending}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) createMutation.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Novo Cliente
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tenant-name">Nome da empresa *</Label>
                <Input
                  id="tenant-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Empresa XYZ Ltda"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tenant-slug">Slug (URL)</Label>
                <Input
                  id="tenant-slug"
                  value={slug}
                  onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }}
                  placeholder="empresa-xyz"
                  className="font-mono"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tenant-admin-email">Email do administrador *</Label>
                <Input
                  id="tenant-admin-email"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@empresa.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tenant-admin-password">Senha do administrador *</Label>
                <Input
                  id="tenant-admin-password"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Mín. 8 caracteres"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenant-plan">Plano</Label>
              <Select value={planId} onValueChange={setPlanId}>
                <SelectTrigger id="tenant-plan">
                  <SelectValue placeholder="Sem plano por enquanto" />
                </SelectTrigger>
                <SelectContent>
                  {plans.filter((p) => p.isActive).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label} — R$ {Number(p.basePriceBrl).toFixed(0)}/mês
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => guardedOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={!canSubmit}
              loading={createMutation.isPending}
            >
              Criar cliente
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Formulário inline: Gerenciar Créditos (dentro da aba, sem modal por cima do detalhe) ───

function CreditsForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [operation, setOperation] = useState<'add' | 'remove'>('add');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  const creditsMutation = useMutation({
    mutationFn: () =>
      subscriptionsAdminService.manageCredits(tenantId, {
        amount: Number(amount),
        operation,
        notes: notes || undefined,
      }),
    onSuccess: (data) => {
      toast.success(`Créditos atualizados. Saldo: R$ ${data.creditsBrl.toFixed(2).replace('.', ',')}`);
      qc.invalidateQueries({ queryKey: ['system', 'billing', tenantId] });
      onDone();
    },
  });

  const amountNum = Number(amount);
  const canSubmit = !isNaN(amountNum) && amountNum > 0;

  return (
    <form
      className="space-y-4 rounded-lg border border-gray-800 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) creditsMutation.mutate();
      }}
    >
      <h4 className="flex items-center gap-2 text-sm font-semibold">
        <Wallet className="h-4 w-4" />
        Gerenciar Créditos
      </h4>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="credits-operation">Operação</Label>
          <Select value={operation} onValueChange={(v) => setOperation(v as 'add' | 'remove')}>
            <SelectTrigger id="credits-operation">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="add">Adicionar créditos</SelectItem>
              <SelectItem value="remove">Remover créditos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="credits-amount">Valor (R$) *</Label>
          <Input
            id="credits-amount"
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="50.00"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="credits-notes">Observação</Label>
        <Input
          id="credits-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Cortesia, ajuste, etc."
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant={operation === 'remove' ? 'destructive' : 'default'}
          disabled={!canSubmit}
          loading={creditsMutation.isPending}
        >
          {operation === 'remove' ? 'Remover créditos' : 'Adicionar créditos'}
        </Button>
      </div>
    </form>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

function SystemTenantsPage() {
  const [selectedTenant, setSelectedTenant] = useState<TenantSummary | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('features');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TenantSummary | null>(null);

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['system', 'tenants'],
    queryFn: () => tenantAdminService.listTenants(),
  });

  if (isLoading) {
    return <div className="p-6 text-gray-400">Carregando tenants…</div>;
  }

  const active = tenants.filter((t) => t.active);
  const inactive = tenants.filter((t) => !t.active);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="h-6 w-6 text-indigo-400" />
            Tenants
          </h1>
          <p className="text-gray-400 mt-1">
            Gerencie todos os clientes contratantes e seus módulos habilitados.
          </p>
        </div>
        <Button
          className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-4 w-4" />
          Novo Cliente
        </Button>
      </div>

      <div className="flex gap-3">
        <Badge className="bg-indigo-600 text-white px-3 py-1 text-sm">
          {active.length} ativos
        </Badge>
        <Badge variant="outline" className="border-gray-700 text-gray-300 px-3 py-1 text-sm">
          {inactive.length} inativos
        </Badge>
        <Badge variant="secondary" className="px-3 py-1 text-sm">
          {tenants.length} total
        </Badge>
      </div>

      <div className="grid gap-3">
        {tenants.map((tenant) => (
          <Card
            key={tenant.tenantId}
            className="cursor-pointer bg-gray-900 border-gray-800 hover:border-indigo-500/50 transition-colors"
            onClick={() => {
              setSelectedTenant(tenant);
              setDetailTab('features');
            }}
          >
            <CardContent className="flex items-center justify-between py-4 px-5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-gray-800 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-white leading-none">{tenant.tenantName}</p>
                  <p className="text-xs text-gray-500 mt-1 font-mono">{tenant.tenantSlug}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge
                  className={
                    tenant.active ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'
                  }
                >
                  {tenant.active ? 'Ativo' : 'Inativo'}
                </Badge>
                <ChevronRight className="h-4 w-4 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <CreateTenantDialog open={createOpen} onOpenChange={setCreateOpen} />

      {deleteTarget && (
        <DeleteTenantDialog
          tenant={deleteTarget}
          open={!!deleteTarget}
          onOpenChange={(v) => !v && setDeleteTarget(null)}
          onDeleted={() => setSelectedTenant(null)}
        />
      )}

      {selectedTenant && (
        <Dialog open onOpenChange={(open) => !open && setSelectedTenant(null)}>
          <DialogContent size="xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                <span className="flex-1 min-w-0 truncate">
                  {selectedTenant.tenantName}
                  <span className="text-xs text-muted-foreground font-mono font-normal ml-2">
                    {selectedTenant.tenantSlug}
                  </span>
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-red-500 hover:text-red-400 hover:bg-red-950/40 flex-shrink-0"
                  title="Excluir tenant"
                  aria-label="Excluir tenant"
                  onClick={() => setDeleteTarget(selectedTenant)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </DialogTitle>
            </DialogHeader>

            <div className="flex gap-2 border-b pb-3">
              {(
                [
                  { key: 'features', label: 'Módulos', icon: LayoutGrid },
                  { key: 'subscription', label: 'Assinatura', icon: CreditCard },
                  { key: 'health', label: 'Saúde', icon: Activity },
                  { key: 'users', label: 'Usuários', icon: Users },
                ] as { key: DetailTab; label: string; icon: any }[]
              ).map(({ key, label, icon: Icon }) => (
                <Button
                  key={key}
                  variant={detailTab === key ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDetailTab(key)}
                  className="flex items-center gap-1.5"
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </Button>
              ))}
            </div>

            {detailTab === 'features' && (
              <><McpOwnerPanel key={selectedTenant.tenantId} tenantId={selectedTenant.tenantId} /><FeaturesPanel tenantId={selectedTenant.tenantId} /></>
            )}
            {detailTab === 'subscription' && (
              <SubscriptionPanel tenantId={selectedTenant.tenantId} />
            )}
            {detailTab === 'health' && (
              <HealthPanel tenantId={selectedTenant.tenantId} />
            )}
            {detailTab === 'users' && (
              <UsersPanel tenantId={selectedTenant.tenantId} />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ─── Aba Usuários ─────────────────────────────────────────────────────────────

function UsersPanel({ tenantId }: { tenantId: string }) {
  const [search, setSearch] = useState('');
  const [sendingId, setSendingId] = useState<string | null>(null);

  const { data: users = [], isLoading } = useQuery<TenantAdminUser[]>({
    queryKey: ['system', 'admin-users', tenantId, search],
    queryFn: () => tenantAdminService.listAdminUsers(tenantId, search || undefined),
    staleTime: 30_000,
  });

  const handleSendReset = async (user: TenantAdminUser) => {
    setSendingId(user.id);
    try {
      await tenantAdminService.sendPasswordReset(tenantId, user.email);
      toast.success(`Email de redefinição enviado para ${user.email}`);
    } catch {
      // toast de erro vem do interceptor
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          className="pl-9"
          placeholder="Buscar por nome, email ou ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Carregando…</p>
      ) : users.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          {search ? 'Nenhum usuário encontrado para esta busca.' : 'Nenhum usuário admin neste tenant.'}
        </p>
      ) : (
        <div className="divide-y rounded-lg border border-gray-800">
          {users.map((user) => (
            <div key={user.id} className="flex items-center justify-between px-4 py-3 gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-8 w-8 rounded-full bg-indigo-900/60 border border-indigo-700 flex items-center justify-center flex-shrink-0">
                  {user.active ? (
                    <UserCheck className="h-4 w-4 text-indigo-400" />
                  ) : (
                    <UserX className="h-4 w-4 text-gray-500" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user.fullName ?? <span className="text-gray-500 italic">Sem nome</span>}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{user.email}</p>
                  <p className="text-[10px] text-gray-600 font-mono truncate">{user.id}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!user.active && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-gray-600 text-gray-500">
                    inativo
                  </Badge>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={sendingId === user.id || !user.active}
                  onClick={() => handleSendReset(user)}
                  className="flex items-center gap-1.5 border-gray-700 text-gray-300 hover:border-indigo-500 hover:text-indigo-300"
                  title="Enviar email de redefinição de senha"
                >
                  {sendingId === user.id ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <KeyRound className="h-3.5 w-3.5" />
                  )}
                  Reset senha
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Apenas usuários com papel <span className="font-mono">admin</span> são listados aqui.
        Os demais usuários podem ter a senha redefinida pelos admins de cada tenant.
      </p>
    </div>
  );
}

// ─── Aba Módulos (feature flags com override manual) ──────────────────────────

function FeaturesPanel({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const { data: flags = [], isLoading } = useQuery({
    queryKey: ['system', 'features', tenantId],
    queryFn: () => tenantAdminService.listFeatures(tenantId),
  });

  const toggleMutation = useMutation({
    mutationFn: (flag: FeatureFlag) =>
      tenantAdminService.upsertFeature(tenantId, {
        featureKey: flag.featureKey,
        isEnabled: !flag.isEnabled,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['system', 'features', tenantId] }),
  });

  if (isLoading)
    return <p className="text-sm text-muted-foreground py-4">Carregando módulos…</p>;

  const enabled = flags.filter((f) => f.isEnabled).length;
  const sortedFlags = [...flags].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {enabled} de {flags.length} módulos habilitados · clique em um módulo para ver permissões e
        itens de menu
      </p>
      <div className="rounded-lg border overflow-hidden">
        {sortedFlags.map((flag) => (
          <FeatureModuleExpandableRow
            key={flag.featureKey}
            featureKey={flag.featureKey}
            label={flag.label}
            expanded={expandedKey === flag.featureKey}
            onToggle={() =>
              setExpandedKey((prev) => (prev === flag.featureKey ? null : flag.featureKey))
            }
            badges={
              <>
                {flag.source === 'addon' && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 h-4 border-indigo-400 text-indigo-400"
                  >
                    addon
                  </Badge>
                )}
                {flag.source === 'manual' && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 h-4 border-yellow-400 text-yellow-400"
                  >
                    override
                  </Badge>
                )}
                {!flag.isEnabled && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                    inativo
                  </Badge>
                )}
              </>
            }
            trailing={
              <Switch
                checked={flag.isEnabled}
                disabled={toggleMutation.isPending}
                onCheckedChange={() => toggleMutation.mutate(flag)}
              />
            }
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Override manual tem prioridade sobre a assinatura de add-on.
      </p>
    </div>
  );
}

// ─── Aba Assinatura ────────────────────────────────────────────────────────────

export function SubscriptionPanel({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const [creditsOpen, setCreditsOpen] = useState(false);

  const { data: billing, isLoading: billingLoading } = useQuery<BillingSummary>({
    queryKey: ['system', 'billing', tenantId],
    queryFn: () => subscriptionsAdminService.getTenantBilling(tenantId),
  });

  const { data: plans = [] } = useQuery<Plan[]>({
    queryKey: ['system', 'plans'],
    queryFn: () => subscriptionsAdminService.listPlans(),
  });

  const { data: allAddons = [] } = useQuery<PlanAddon[]>({
    queryKey: ['system', 'addons'],
    queryFn: () => subscriptionsAdminService.listAddons(),
  });

  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [userSlots, setUserSlots] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');

  const assignPlanMutation = useMutation({
    mutationFn: () =>
      subscriptionsAdminService.assignSubscription(tenantId, {
        planId: selectedPlanId,
        userSlots: userSlots ? Number(userSlots) : undefined,
        billingCycle,
      }),
    onSuccess: () => {
      toast.success('Assinatura atribuída com sucesso');
      qc.invalidateQueries({ queryKey: ['system', 'billing', tenantId] });
      qc.invalidateQueries({ queryKey: ['system', 'features', tenantId] });
      setSelectedPlanId('');
      setUserSlots('');
    },
  });

  const toggleAddonMutation = useMutation({
    mutationFn: ({ addon, active }: { addon: TenantAddon | PlanAddon; active: boolean }) => {
      if (active) {
        return subscriptionsAdminService.cancelAddon(tenantId, (addon as TenantAddon).addon.id);
      } else {
        return subscriptionsAdminService.assignAddon(tenantId, (addon as PlanAddon).id);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['system', 'billing', tenantId] });
      qc.invalidateQueries({ queryKey: ['system', 'features', tenantId] });
    },
  });

  if (billingLoading)
    return <p className="text-sm text-muted-foreground py-4">Carregando assinatura…</p>;

  const activeAddonIds = new Set(
    billing?.addons.filter((a) => a.status === 'active').map((a) => a.addon.id) ?? [],
  );

  const activeAddonMap = new Map(
    billing?.addons
      .filter((a) => a.status === 'active')
      .map((a) => [a.addon.id, a]) ?? [],
  );

  const creditsBrl = billing?.creditsBrl ?? 0;
  const totalAfterCredits = billing?.breakdown?.totalAfterCredits ?? 0;

  return (
    <div className="space-y-6">
      {/* Resumo atual */}
      {billing?.plan ? (
        <div className="rounded-lg border bg-indigo-950/30 border-indigo-800/50 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-indigo-400" />
              <span className="font-medium text-white">{billing.plan.label}</span>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-white">
                R$ {billing.breakdown.total.toFixed(2).replace('.', ',')}
                <span className="text-sm font-normal text-gray-400">/mês</span>
              </p>
              {creditsBrl > 0 && (
                <p className="text-sm text-green-400 font-medium">
                  → R$ {totalAfterCredits.toFixed(2).replace('.', ',')} c/ créditos
                </p>
              )}
            </div>
          </div>
          <div className="grid gap-2 text-sm sm:grid-cols-3">
            <div className="text-center p-2 rounded bg-gray-900/60">
              <p className="text-gray-400 text-xs">Base</p>
              <p className="text-white font-medium">R$ {billing.breakdown.base.toFixed(0)}</p>
            </div>
            <div className="text-center p-2 rounded bg-gray-900/60">
              <p className="text-gray-400 text-xs">
                {billing.userSlots} usuários (+{billing.extraUsers} extra)
              </p>
              <p className="text-white font-medium">R$ {billing.breakdown.users.toFixed(0)}</p>
            </div>
            <div className="text-center p-2 rounded bg-gray-900/60">
              <p className="text-gray-400 text-xs">Add-ons</p>
              <p className="text-white font-medium">R$ {billing.breakdown.addons.toFixed(0)}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-700 p-4 text-center text-gray-400 text-sm">
          Nenhuma assinatura ativa. Atribua um plano abaixo.
        </div>
      )}

      {/* Créditos */}
      <div className="flex items-center justify-between rounded-lg border border-gray-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <Wallet className="h-4 w-4 text-green-400" />
          <div>
            <p className="text-sm font-medium text-white">Créditos disponíveis</p>
            <p className="text-xs text-muted-foreground">Desconto em R$ sobre a fatura</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={creditsBrl > 0 ? 'bg-green-700 text-white' : 'bg-gray-700 text-gray-300'}>
            R$ {creditsBrl.toFixed(2).replace('.', ',')}
          </Badge>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCreditsOpen(true)}
            className="border-gray-700 text-gray-300"
          >
            Gerenciar
          </Button>
        </div>
      </div>

      {creditsOpen && <CreditsForm tenantId={tenantId} onDone={() => setCreditsOpen(false)} />}

      {/* Atribuir / trocar plano */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <Package className="h-4 w-4" />
          {billing?.plan ? 'Trocar Plano' : 'Atribuir Plano'}
        </h4>
        <div className="grid gap-2 sm:grid-cols-3">
          <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
            <SelectTrigger className="col-span-1">
              <SelectValue placeholder="Selecionar plano" />
            </SelectTrigger>
            <SelectContent>
              {plans.filter((p) => p.isActive).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.label} — R$ {Number(p.basePriceBrl).toFixed(0)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <Input
              type="number"
              placeholder={`${billing?.userSlots ?? 3} usuários`}
              value={userSlots}
              onChange={(e) => setUserSlots(e.target.value)}
            />
          </div>

          <Select value={billingCycle} onValueChange={setBillingCycle}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Mensal</SelectItem>
              <SelectItem value="annual">Anual</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={() => assignPlanMutation.mutate()}
          disabled={assignPlanMutation.isPending || !selectedPlanId}
          className="w-full bg-indigo-600 hover:bg-indigo-700"
        >
          {assignPlanMutation.isPending ? (
            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          {billing?.plan ? 'Trocar Plano' : 'Atribuir Plano'}
        </Button>
      </div>

      {/* Add-ons */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <Puzzle className="h-4 w-4" />
          Add-ons
        </h4>
        <div className="divide-y rounded-lg border border-gray-800">
          {allAddons.filter((a) => a.isActive).map((addon) => {
            const isActive = activeAddonIds.has(addon.id);
            const tenantAddon = activeAddonMap.get(addon.id);
            return (
              <div key={addon.id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  {isActive ? (
                    <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-gray-600 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-white">{addon.name}</p>
                    <p className="text-xs text-gray-500 font-mono">{addon.featureKey}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-300">
                    R$ {Number(addon.priceBrl).toFixed(0)}/mês
                  </span>
                  <Switch
                    checked={isActive}
                    disabled={toggleAddonMutation.isPending}
                    onCheckedChange={() =>
                      toggleAddonMutation.mutate({
                        addon: isActive ? tenantAddon! : addon,
                        active: isActive,
                      })
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Aba Saúde ────────────────────────────────────────────────────────────────

function HealthPanel({ tenantId }: { tenantId: string }) {
  const { data: health, isLoading } = useQuery<TenantHealthReport>({
    queryKey: ['system', 'health', tenantId],
    queryFn: () => tenantAdminService.getTenantHealth(tenantId),
  });

  if (isLoading)
    return <p className="text-sm text-muted-foreground py-4">Carregando dados…</p>;
  if (!health) return null;

  const hasIntegrityIssues =
    health.integrity.usersWithoutRole > 0 || health.integrity.orphanedPermissions > 0;

  const counts = [
    { label: 'Usuários', value: health.counts.users },
    { label: 'Times', value: health.counts.teams },
    { label: 'Clientes', value: health.counts.clients },
    { label: 'Assets', value: health.counts.assets },
    { label: 'Roles', value: health.counts.roles },
    { label: 'Senhas', value: health.counts.passwords },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        {counts.map((c) => (
          <div key={c.label} className="rounded-lg border p-3 text-center">
            <p className="text-2xl font-bold">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      <Card className={hasIntegrityIssues ? 'border-yellow-500/50' : 'border-green-500/50'}>
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            {hasIntegrityIssues ? (
              <TriangleAlert className="h-4 w-4 text-yellow-500" />
            ) : (
              <ShieldCheck className="h-4 w-4 text-green-500" />
            )}
            Integridade de dados
          </CardTitle>
        </CardHeader>
        <div className="px-4 pb-3 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Usuários sem permissão</span>
            <Badge
              variant={health.integrity.usersWithoutRole > 0 ? 'destructive' : 'outline'}
            >
              {health.integrity.usersWithoutRole}
            </Badge>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Permissões órfãs</span>
            <Badge
              variant={
                health.integrity.orphanedPermissions > 0 ? 'destructive' : 'outline'
              }
            >
              {health.integrity.orphanedPermissions}
            </Badge>
          </div>
        </div>
      </Card>
    </div>
  );
}
